import { Network as AptosNetwork } from '@aptos-labs/ts-sdk'
import { createConfig } from '@privy-io/wagmi'
import { type Chain, defineChain } from 'viem'
import { http, type Transport } from 'wagmi'
import {
  avalanche,
  ink,
  mainnet,
  optimism,
  optimismSepolia,
  plumeMainnet as plume,
  polygon,
  polygonAmoy,
  sei,
  sepolia,
} from 'wagmi/chains'

import type { MultiSdkName } from '../queries/redeem/blockchain/management'
import type { BlockchainNetwork } from '../queries/redeem/redeemables'

const hardhat = defineChain({
  id: 31_337,
  name: 'Hardhat',
  nativeCurrency: {
    decimals: 18,
    name: 'Ether',
    symbol: 'ETH',
  },
  rpcUrls: {
    default: { http: ['http://127.0.0.1:8545'] },
  },
  contracts: {
    multicall3: {
      address: '0xca11bde05977b3631167028862be2a173976ca11',
      blockCreated: 2,
    },
  },
})

export const get_RPC_url = (network: string) => {
  switch (network) {
    case 'plume-mainnet':
      return 'https://rpc.plume.org'
    case 'aptos-testnet':
      return `https://api.testnet.aptoslabs.com/v1`
    case 'aptos-mainnet':
      return `https://api.mainnet.aptoslabs.com/v1`
    default:
      return `https://${network}.g.alchemy.com/v2/${import.meta.env.VITE_ALCHEMY_KEY}`
  }
}

export { type BlockchainNetwork }

export type BaseConfig = {
  name: string
  type: MultiSdkName
  rpc_name: string
  scan_url_maker: (tx: string) => string
}

export type BlockchainConfigWagmi = BaseConfig & {
  type: Extract<MultiSdkName, 'wagmi'>
  chain: Chain
}

export type BlockchainConfigSolanaKit = BaseConfig & {
  type: Extract<MultiSdkName, 'solana-kit'>
  chain: {
    id: `solana:${string}`
  }
}

export type BlockchainConfigAptos = BaseConfig & {
  type: Extract<MultiSdkName, 'aptos'>
  aptos_network_name: AptosNetwork.MAINNET | AptosNetwork.TESTNET
  chain: {
    id: `aptos:${string}`
  }
}

export type BlockchainConfig =
  | BlockchainConfigWagmi
  | BlockchainConfigSolanaKit
  | BlockchainConfigAptos
  | {
      name: string
      type: 'unsupported'
      rpc_name?: string
      scan_url_maker: (tx: string) => string
    }

export const chain_configs: Record<BlockchainNetwork, BlockchainConfig> = {
  ethereum: {
    name: 'Ethereum',
    type: 'wagmi',
    chain: mainnet,
    rpc_name: 'eth-mainnet',
    scan_url_maker: (tx: string) => `https://etherscan.io/search?q=${tx}`,
  },
  ethereum_sepolia: {
    name: 'Sepolia',
    type: 'wagmi',
    chain: sepolia,
    rpc_name: 'eth-sepolia',
    scan_url_maker: (tx: string) => `https://sepolia.etherscan.io/tx/${tx}`,
  },
  optimism: {
    name: 'Optimism',
    type: 'wagmi',
    chain: optimism,
    rpc_name: 'opt-mainnet',
    scan_url_maker: (tx: string) => `https://optimistic.etherscan.io/tx/${tx}`,
  },
  optimism_sepolia: {
    name: 'Optimism Sepolia',
    type: 'wagmi',
    chain: optimismSepolia,
    rpc_name: 'opt-sepolia',
    scan_url_maker: (tx: string) =>
      `https://sepolia-optimistic.etherscan.io/tx/${tx}`,
  },
  sei: {
    name: 'SEI',
    type: 'wagmi',
    chain: sei,
    rpc_name: 'sei-mainnet',
    scan_url_maker: (tx: string) => `https://seitrace.com/tx/${tx}`,
  },
  polygon: {
    name: 'Polygon',
    type: 'wagmi',
    chain: polygon,
    rpc_name: 'polygon-mainnet',
    scan_url_maker: (tx: string) => `https://polygonscan.com/tx/${tx}`,
  },
  polygon_amoy: {
    name: 'Polygon Amoy',
    type: 'wagmi',
    chain: polygonAmoy,
    rpc_name: 'polygon-amoy',
    scan_url_maker: (tx: string) => `https://amoy.polygonscan.com/tx/${tx}`,
  },
  avalanche: {
    name: 'Avalanche',
    type: 'wagmi',
    chain: avalanche,
    rpc_name: 'avax-mainnet',
    scan_url_maker: (tx: string) => `https://snowtrace.io/tx/${tx}`,
  },
  hardhat: {
    name: 'Hardhat',
    type: 'wagmi',
    chain: hardhat,
    rpc_name: 'hardhat',
    scan_url_maker: (_tx: string) => ``,
  },
  solana: {
    name: 'Solana',
    type: 'solana-kit',
    chain: {
      id: 'solana:mainnet',
    },
    rpc_name: 'solana-mainnet',
    scan_url_maker: (tx: string) => `https://solscan.io/tx/${tx}`,
  },
  solana_devnet: {
    name: 'Solana Devnet',
    type: 'solana-kit',
    chain: {
      id: 'solana:devnet',
    },
    rpc_name: 'solana-devnet',
    scan_url_maker: (tx: string) =>
      `https://solscan.io/tx/${tx}?cluster=devnet`,
  },
  plume: {
    name: 'Plume',
    type: 'wagmi',
    chain: plume,
    rpc_name: 'plume-mainnet',
    scan_url_maker: (tx: string) => `https://explorer.plume.org/tx/${tx}`,
  },
  ink: {
    name: 'Ink',
    type: 'wagmi',
    chain: ink,
    rpc_name: 'ink-mainnet',
    scan_url_maker: (tx: string) => `https://explorer.inkonchain.com/tx/${tx}`,
  },
  aptos: {
    name: 'Aptos',
    type: 'aptos',
    chain: {
      id: 'aptos:testnet',
    },
    rpc_name: 'aptos-mainnet',
    aptos_network_name: AptosNetwork.MAINNET,
    scan_url_maker: (tx: string) => `https://explorer.aptoslabs.com/txn/${tx}`,
  },
  aptos_testnet: {
    name: 'Aptos',
    type: 'aptos',
    chain: {
      id: 'aptos:testnet',
    },
    rpc_name: 'aptos-testnet',
    aptos_network_name: AptosNetwork.TESTNET,
    scan_url_maker: (tx: string) =>
      `https://explorer.aptoslabs.com/txn/${tx}?network=testnet`,
  },
} as const

export const standard_blockchain_wait = 5000

export const active_chains_configs = Object.values(chain_configs).filter(
  (c): c is BlockchainConfigWagmi => c.type === 'wagmi',
)

export function make_config() {
  return createConfig({
    batch: { multicall: false },
    chains: active_chains_configs.map((c) => c.chain) as [Chain, ...Chain[]],
    transports: {
      ...active_chains_configs.reduce<Record<Chain['id'], Transport>>(
        (acc, c) => {
          acc[c.chain.id] = http(get_RPC_url(c.rpc_name))
          return acc
        },
        {},
      ),
      [hardhat.id]: http('http://127.0.0.1:8545/'),
    },
  })
}

export function make_aptos_config() {
  return {
    network: AptosNetwork.MAINNET,
    aptosApiKeys: {
      testnet: import.meta.env.VITE_GEOMI_KEY,
      mainnet: import.meta.env.VITE_GEOMI_KEY,
    },
  }
}
